import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  searchOrgTeams,
  type OrgSearchTeam,
} from '@sportninja/common/services/team/orgTeamSearchService'

interface Args {
  orgId: string
  rootScheduleId: string
  query: string
  isGlobal: boolean
  /** Defaults to the first page — the dropdown shows one page of matches. */
  page?: number
  sportId?: string | null
  /**
   * Generic-sport competitions filter by this instead of `sportId`. Legacy sent
   * it on the same search (`search-menu-helpers.js` appends `generic_sport_id`),
   * so omitting it returns teams from every sport.
   */
  genericSportId?: string | null
  enabled?: boolean
  debounceMs?: number
}

export interface OrgTeamSearch {
  teams: OrgSearchTeam[]
  totalPages: number
  isFetching: boolean
}

const useDebouncedValue = <T>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    if (delay === 0) {
      setDebounced(value)
      return
    }
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])

  return debounced
}

/**
 * The legacy Setup tab's team search, behind React Query.
 *
 * An empty query is deliberately allowed and fires immediately — the legacy
 * panel listed page 1 of the organization's teams before anything was typed,
 * and that browse-first behaviour is what makes the picker usable.
 */
export const useOrgTeamSearch = ({
  orgId,
  rootScheduleId,
  query,
  isGlobal,
  page = 1,
  sportId,
  genericSportId,
  enabled = true,
  debounceMs = 300,
}: Args): OrgTeamSearch => {
  'use memo'

  const debouncedQuery = useDebouncedValue(query, query === '' ? 0 : debounceMs)

  const { data, isFetching } = useQuery({
    queryKey: queryKeys.teamSearch.byOrg(
      orgId,
      rootScheduleId,
      debouncedQuery,
      isGlobal,
      page,
      sportId,
      genericSportId
    ),
    queryFn: ({ signal }) =>
      searchOrgTeams({
        orgId,
        rootScheduleId,
        query: debouncedQuery,
        isGlobal,
        page,
        sportId,
        genericSportId,
        signal,
      }),
    enabled: enabled && !!orgId && !!rootScheduleId,
    staleTime: 30_000,
    // Keeps the current page on screen while the next one loads, so paging
    // does not flash an empty list.
    placeholderData: keepPreviousData,
  })

  return {
    teams: data?.teams ?? [],
    totalPages: data?.totalPages ?? 1,
    isFetching,
  }
}
